import { Button, Link } from "@aws-amplify/ui-react"
import type { UserDataDto } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { type PropsWithChildren, useEffect, useState } from "react"
import { FaArrowUpRightFromSquare, FaBars, FaXmark } from "react-icons/fa6"
import { useLocation } from "react-router-dom"
import { isProd, isStaging, stageName } from "../config"
import { AdminToggle } from "./AdminToggle"
import { OnboardingSlot } from "./onboarding/OnboardingSlot"
import { MainMenu } from "."

interface AppLayoutWrapperProps extends PropsWithChildren {
  userData: UserDataDto | undefined
  activeOrgId: string | undefined
  signOutHandler: () => Promise<void>
}

export const AppLayoutWrapper = ({
  userData,
  activeOrgId,
  signOutHandler,
  children,
}: AppLayoutWrapperProps) => {
  const location = useLocation()
  const [menuToggled, setMenuToggled] = useState(false)

  useEffect(() => {
    setMenuToggled(false)
  }, [location.pathname])

  return (
    <>
      <header className="app-header">
        <div className="flex gap-x-6 items-center">
          <button
            type="button"
            className="hidden max-[1024px]:block text-white"
            aria-label={
              menuToggled ? I18n.get("Menü schließen") : I18n.get("Menü öffnen")
            }
            onClick={() => setMenuToggled((toggled) => !toggled)}
          >
            {menuToggled ? (
              <FaXmark className="h-5 w-5" />
            ) : (
              <FaBars className="h-5 w-5" />
            )}
          </button>

          <h1 className="flex gap-x-6 items-center">
            <img
              src="/logo-strautmann.svg"
              alt="Strautmann"
              className="!h-[26px] sm:!h-[34px]"
            />
            {!isProd && (
              <>
                <div>{`SDS /${stageName}`}</div>
                {!isStaging && (
                  <Link
                    href={
                      "https://staging.sds.strautmann.cloud" + location.pathname
                    }
                  >
                    <Button variation="primary" colorTheme="info" size="small">
                      <FaArrowUpRightFromSquare className="inline mr-1" />
                      staging
                    </Button>
                  </Link>
                )}
                <Link href={"https://sds.strautmann.cloud" + location.pathname}>
                  <Button variation="primary" colorTheme="warning" size="small">
                    <FaArrowUpRightFromSquare className="inline mr-1" />
                    prod
                  </Button>
                </Link>
              </>
            )}
          </h1>
        </div>

        <div className="user-account flex justify-end items-center gap-x-3">
          {/* TODO: avatar image */}
          {/* <div>(AV)</div> */}
          {userData && (
            <div className="hidden sm:flex items-center gap-x-1">
              {/* {user?.username} */}
              {userData.first_name} {userData.last_name}
              <svg
                className="inline"
                width="13"
                height="8"
                viewBox="0 0 13 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <mask
                  id="mask0_140_164"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="13"
                  height="8"
                >
                  <path
                    d="M13 7.6547V0.345703L0.819 0.345703V7.6547L13 7.6547Z"
                    fill="white"
                  />
                </mask>
                <g mask="url(#mask0_140_164)">
                  <path
                    d="M2.03662 1.56348L6.90862 6.43548L11.7816 1.56348"
                    stroke="white"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </div>
          )}
          <AdminToggle />
          <div>
            <button
              onClick={signOutHandler}
              className="inline-flex h-9 px-2 items-center justify-center rounded-md transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
            >
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <MainMenu
        userData={userData}
        activeOrgId={activeOrgId}
        toggled={menuToggled}
        onClose={() => setMenuToggled(false)}
        onBreakPointChange={(broken) => {
          if (!broken) {
            setMenuToggled(false)
          }
        }}
      />
      <main className="main-container">
        <OnboardingSlot userData={userData} />
        {children}
      </main>
    </>
  )
}
